<script setup>
/**
 * The session you are running, when it is about to start or already has
 * (prototype, Overview as a broker: "Protecting your home in severe weather
 * starts in 8 minutes", with Open the room). Only a session the viewer hosts —
 * the row's own action is "open the room" — and never the Office Hours desk,
 * which is open for hours and says who is waiting in Today instead.
 *
 * The minutes count down on the page's own clock, so a banner left open does
 * not keep saying "in 8 minutes".
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import XlButton from '../../xl/XlButton.vue';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  // How soon "about to start" begins: the greeting's own window.
  withinMinutes: { type: Number, default: 30 },
});

const emit = defineEmits(['open']);

const now = ref(Date.now());
let timer = null;
onMounted(() => { timer = setInterval(() => { now.value = Date.now(); }, 30_000); });
onBeforeUnmount(() => clearInterval(timer));

const minutesUntil = (row) => Math.ceil((new Date(row.start_at).getTime() - now.value) / 60_000);

const row = computed(() => props.rows.find((candidate) => candidate.kind !== 'office_hours'
  && candidate.action?.kind === 'open_room'
  && !candidate.has_ended
  && (candidate.is_live || (minutesUntil(candidate) >= 0 && minutesUntil(candidate) <= props.withinMinutes))) ?? null);

const when = computed(() => {
  if (!row.value) return '';
  const minutes = minutesUntil(row.value);
  if (row.value.is_live || minutes <= 0) return 'is live now';
  return minutes === 1 ? 'starts in a minute' : `starts in ${minutes} minutes`;
});
</script>

<template>
  <div v-if="row" class="starting" data-testid="overview-starting-soon">
    <span class="starting__ico" aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6.5" width="12.5" height="11" rx="2.5" /><path d="m15.5 10.5 5-3v9l-5-3" /></svg>
    </span>
    <p class="starting__text">
      <b>{{ row.title }}</b> {{ when }}
    </p>
    <XlButton size="sm" data-testid="overview-starting-open" @click="emit('open', row)">Open the room</XlButton>
  </div>
</template>

<style scoped>
/* The prototype's tinted strip above Today: the brand's lightest tint, its
   border, a white glyph square, the line, and the one action. */
.starting {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
  padding: 14px 16px;
  border: 1px solid var(--xl-color-primary-border, #D9C6E8);
  border-radius: 16px;
  background: var(--xl-color-primary-container, #F5EFF9);
  font-family: var(--xl-font-family);
}

.starting__ico {
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 11px;
  background: #fff;
  color: var(--xl-color-primary);
}

.starting__text {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--xl-color-on-surface-variant);
}

.starting__text b {
  color: var(--xl-color-on-surface);
  font-weight: var(--xl-font-weight-semibold, 600);
}
</style>
